import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import {
  ClipboardList, RefreshCw, Plus, Trash2, FileText, AlertTriangle,
  CheckCircle2, Settings, Download, PenLine, ShieldAlert, X, Users, UserPlus,
  ScanLine, Upload, Eye, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Can from '../components/Can';
import Title31RangeReport from '../components/Title31RangeReport';
import { useAuth } from '../contexts/AuthContext';

// ----------------------------------------------------------------
// Title 31 (BSA) — Multiple Transaction Log (MTL) + CTR
// ----------------------------------------------------------------
// Digitizes the paper "El Dorado Hills Casino Multiple Transaction Log":
// one patron-day sheet (identity + physical description + table/seat +
// key employee + notes + CTR/SAR flags), timed & initialed cash-in / cash-out
// lines, running cash-in / cash-out totals kept SEPARATE, per-gaming-day CTR
// flagging at the $10k threshold, and FinCEN Form 112 batch XML export.

const fmtCents = (c) =>
  (Number(c || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dollarsToCents = (v) => Math.round(Number(v || 0) * 100);
const centsToDollars = (c) => (Number(c || 0) / 100);

const DESC_FIELDS = [
  ['sex', 'Sex'], ['race', 'Race'], ['age', 'Age'], ['height', 'Height'],
  ['weight', 'Weight'], ['eyeColor', 'Eye Color'], ['hairColor', 'Hair Color'],
  ['glasses', 'Glasses'], ['facialHair', 'Facial Hair'], ['shirt', 'Shirt Type/Color'],
  ['pants', 'Pant Type/Color'], ['shoes', 'Shoe Type/Color'], ['otherDescriptors', 'Other Descriptors'],
];
const ID_FIELDS = [
  ['name', 'Name'], ['address', 'Address'], ['city', 'City'], ['state', 'State'],
  ['postalCode', 'ZIP'], ['dateOfBirth', 'Date of Birth'], ['idType', 'ID Type'],
  ['idNumber', 'ID Number'], ['idIssuer', 'ID Issuer (State/Country)'], ['tin', 'SSN / TIN'],
];

export default function CageMtl() {
  const { can } = useAuth();
  const canOperate = can('title31:operate');
  const canFile = can('title31:file');

  const [date, setDate] = useState(todayStr());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [line, setLine] = useState({ direction: 'in', amount: '', timeText: '', initials: '', txnType: '' });
  const amtRef = useRef(null);

  const [config, setConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [configForm, setConfigForm] = useState(null);
  const [exportResult, setExportResult] = useState(null);

  const ctrThresholdCents = (config && config.ctrThresholdCents) || 1000000;

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, cfgRes] = await Promise.all([
        api.get('/admin/title31/summary', { params: { date } }),
        api.get('/admin/title31/config'),
      ]);
      setSummary(sumRes.data);
      setConfig(cfgRes.data.config);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load MTL');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const openSheet = async (id) => {
    setSelectedId(id);
    setExportResult(null);
    try {
      const { data } = await api.get(`/admin/title31/sheets/${id}`);
      setSheet(data.sheet);
      setForm(data.sheet);
    } catch (e) {
      toast.error('Failed to open sheet');
    }
  };

  const newSheet = async () => {
    try {
      const { data } = await api.post('/admin/title31/sheets', { gamingDate: date });
      await loadSummary();
      openSheet(data.sheet.id);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to create sheet');
    }
  };

  const saveSheet = async () => {
    if (!sheet) return;
    setSaving(true);
    try {
      const payload = {};
      [...ID_FIELDS, ...DESC_FIELDS].forEach(([k]) => { payload[k] = form[k] ?? null; });
      // SSN and ID number come back from the server masked (•••••••6789). Posting
      // a mask back would ask the server to store dots as the value. Send
      // undefined so the field is left exactly as it is unless the banker typed
      // something new. (The server ignores masks too - this is the near side of
      // the same rule.)
      for (const k of ['tin', 'idNumber']) {
        if (typeof payload[k] === 'string' && /^[•*]/.test(payload[k])) delete payload[k];
      }
      ['country', 'tableNumber', 'seatNumber', 'keyEmployee', 'notes', 'memberId'].forEach((k) => { payload[k] = form[k] ?? null; });
      payload.sarFlagged = !!form.sarFlagged;
      const { data } = await api.put(`/admin/title31/sheets/${sheet.id}`, payload);
      setSheet(data.sheet); setForm(data.sheet);
      toast.success('Sheet saved');
      loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const signSheet = async () => {
    try {
      const { data } = await api.post(`/admin/title31/sheets/${sheet.id}/sign`, {});
      setSheet(data.sheet); setForm(data.sheet);
      toast.success('Sheet signed');
    } catch (e) { toast.error('Sign failed'); }
  };

  const deleteSheet = async (id) => {
    try {
      await api.delete(`/admin/title31/sheets/${id}`);
      if (selectedId === id) { setSelectedId(null); setSheet(null); }
      toast.success('Sheet deleted');
      loadSummary();
    } catch (e) { toast.error(e?.response?.data?.error || 'Delete failed'); }
  };

  const addLine = async (e) => {
    if (e) e.preventDefault();
    if (!sheet) return;
    const cents = dollarsToCents(line.amount);
    if (!cents || cents <= 0) { toast.error('Enter an amount'); return; }
    try {
      const { data } = await api.post(`/admin/title31/sheets/${sheet.id}/transactions`, {
        direction: line.direction,
        amountCents: cents,
        timeText: line.timeText || null,
        initials: line.initials || null,
        txnType: line.txnType || null,
      });
      setSheet(data.sheet); setForm((f) => ({ ...f, totalCashInCents: data.sheet.totalCashInCents, totalCashOutCents: data.sheet.totalCashOutCents }));
      setLine((l) => ({ ...l, amount: '', timeText: '', initials: '' }));
      if (amtRef.current) amtRef.current.focus();
      loadSummary();
    } catch (err) { toast.error(err?.response?.data?.error || 'Failed to add line'); }
  };

  const deleteLine = async (txnId) => {
    try {
      const { data } = await api.delete(`/admin/title31/transactions/${txnId}`);
      setSheet(data.sheet);
      loadSummary();
    } catch (e) { toast.error('Failed to delete line'); }
  };

  const generateCtr = async (id) => {
    try {
      await api.post(`/admin/title31/sheets/${id}/ctr`, {});
      toast.success('CTR generated (draft)');
      loadSummary();
      if (selectedId === id) openSheet(id);
    } catch (e) { toast.error(e?.response?.data?.error || 'CTR generation failed'); }
  };

  const exportBatch = async () => {
    setExportResult(null);
    try {
      const { data } = await api.post('/admin/title31/ctrs/export', { gamingDate: date });
      setExportResult(data);
      if (data.ok && data.xml) {
        const blob = new Blob([data.xml], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = data.filename || 'CTRX_batch.xml';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Batch XML exported (${data.activityCount} CTR${data.activityCount === 1 ? '' : 's'})`);
        loadSummary();
      } else {
        toast.error('Export blocked — resolve validation errors');
      }
    } catch (e) { toast.error(e?.response?.data?.error || 'Export failed'); }
  };

  const openConfig = () => {
    setConfigForm({
      ...config,
      mtlLogFloorDollars: centsToDollars(config.mtlLogFloorCents),
      ctrThresholdDollars: centsToDollars(config.ctrThresholdCents),
    });
    setShowConfig(true);
  };
  const saveConfig = async () => {
    try {
      const payload = {
        mtlLogFloorCents: dollarsToCents(configForm.mtlLogFloorDollars),
        ctrThresholdCents: dollarsToCents(configForm.ctrThresholdDollars),
        filer: configForm.filer,
        transmitter: configForm.transmitter,
      };
      const { data } = await api.put('/admin/title31/config', payload);
      setConfig(data.config);
      setShowConfig(false);
      toast.success('Title 31 settings saved');
      loadSummary();
    } catch (e) { toast.error(e?.response?.data?.error || 'Save failed'); }
  };

  const pct = (cents) => Math.min(100, Math.round((Number(cents || 0) / ctrThresholdCents) * 100));
  const statusColor = (cents) => {
    const c = Number(cents || 0);
    if (c > ctrThresholdCents) return 'text-red-400';
    if (c >= ctrThresholdCents * 0.75) return 'text-amber-400';
    return 'text-gray-300';
  };

  const patrons = (summary && summary.patrons) || [];
  const counts = (summary && summary.counts) || {};

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="text-amber-400" size={22} />
          <h1 className="text-xl font-semibold text-gray-100">Title 31 — Multiple Transaction Log</h1>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setSelectedId(null); setSheet(null); }}
            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-gray-100" />
          <button onClick={loadSummary} className="p-2 rounded bg-gray-800 border border-gray-700 text-gray-300 hover:text-white" title="Refresh">
            <RefreshCw size={16} />
          </button>
          <Can permission="title31:configure">
            <button onClick={openConfig} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-gray-800 border border-gray-700 text-sm text-gray-300 hover:text-white">
              <Settings size={15} /> Settings
            </button>
          </Can>
        </div>
      </div>

      {/* Day summary bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryTile label="Patrons logged" value={counts.patrons ?? 0} />
        <SummaryTile label="Flagged for CTR" value={counts.flaggedForCtr ?? 0} accent={counts.flaggedForCtr ? 'red' : null} />
        <SummaryTile label="CTRs" value={counts.ctrs ?? 0} accent={counts.ctrs ? 'amber' : null} />
        <SummaryTile label="CTR threshold"
          value={config ? fmtCents(config.ctrThresholdCents) : '—'} sub="exceeds, per gaming day" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* List of patron sheets */}
        <div className="lg:col-span-1">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Sheets · {date}</h2>
            <Can permission="title31:operate">
              <button onClick={newSheet} className="flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300">
                <Plus size={15} /> New
              </button>
            </Can>
          </div>
          {loading ? (
            <p className="text-gray-500 text-sm">Loading…</p>
          ) : patrons.length === 0 ? (
            <p className="text-gray-500 text-sm">No patron sheets for this gaming day.</p>
          ) : (
            <div className="space-y-2">
              {patrons.map((p) => {
                const flagged = p.threshold && p.threshold.triggerType;
                return (
                  <button key={p.id} onClick={() => openSheet(p.id)}
                    className={`w-full text-left rounded-lg border p-3 transition-colors ${
                      selectedId === p.id ? 'border-amber-500 bg-gray-800' : 'border-gray-700 bg-gray-900 hover:bg-gray-800'
                    }`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-100 truncate">{p.name || <span className="italic text-gray-400">Unidentified</span>}</span>
                      <div className="flex items-center gap-1">
                        {p.ctr && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-300 uppercase">CTR {p.ctr.status}</span>}
                        {flagged && !p.ctr && <AlertTriangle size={15} className="text-red-400" />}
                        {p.sarFlagged && <ShieldAlert size={15} className="text-purple-400" title="SAR flagged" />}
                      </div>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span>In: <b className={statusColor(p.totalCashInCents)}>{fmtCents(p.totalCashInCents)}</b></span>
                      <span>Out: <b className={statusColor(p.totalCashOutCents)}>{fmtCents(p.totalCashOutCents)}</b></span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {canFile && (
            <div className="mt-4 rounded-lg border border-gray-700 bg-gray-900 p-3">
              <h3 className="text-sm font-semibold text-gray-200 mb-2 flex items-center gap-1.5"><FileText size={15} /> FinCEN filing</h3>
              <button onClick={exportBatch}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium">
                <Download size={15} /> Export CTR batch XML
              </button>
              <p className="text-[11px] text-gray-500 mt-2">Generates a FinCEN CTRX batch for all CTRs on {date}. Upload it to the BSA E-Filing portal. Validate in the FinCEN test environment before your first real filing.</p>
              {exportResult && <ExportResult r={exportResult} />}
            </div>
          )}
        </div>

        {/* Sheet editor */}
        <div className="lg:col-span-2">
          {!sheet ? (
            <div className="rounded-lg border border-dashed border-gray-700 p-10 text-center text-gray-500">
              Select a sheet, or create a new one.
            </div>
          ) : (
            <SheetEditor
              sheet={sheet} form={form} setForm={setForm}
              canOperate={canOperate} canFile={canFile} saving={saving}
              onSave={saveSheet} onSign={signSheet} onDelete={() => deleteSheet(sheet.id)}
              line={line} setLine={setLine} onAddLine={addLine} onDeleteLine={deleteLine} amtRef={amtRef}
              config={config} ctrThresholdCents={ctrThresholdCents} pct={pct} statusColor={statusColor}
              onGenerateCtr={() => generateCtr(sheet.id)}
            />
          )}
        </div>
      </div>

      {/* The range view. Everything above is one gaming day, which is right for
          the cage; this is the view for a review or an examiner walkthrough. */}
      <Title31RangeReport />

      {showConfig && configForm && (
        <ConfigModal form={configForm} setForm={setConfigForm} onClose={() => setShowConfig(false)} onSave={saveConfig} />
      )}
    </div>
  );
}

function SummaryTile({ label, value, sub, accent }) {
  const color = accent === 'red' ? 'text-red-400' : accent === 'amber' ? 'text-amber-400' : 'text-gray-100';
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-400">{label}</div>
      {sub && <div className="text-[10px] text-gray-500">{sub}</div>}
    </div>
  );
}

function ExportResult({ r }) {
  return (
    <div className="mt-3 text-xs space-y-2">
      {!r.ok && (
        <div className="rounded bg-red-950/50 border border-red-800 p-2">
          <div className="text-red-300 font-medium mb-1">Export blocked</div>
          {(r.validations || []).flatMap((v) => v.errors || []).map((e, i) => (
            <div key={i} className="text-red-200">• {e}</div>
          ))}
        </div>
      )}
      {r.ok && (
        <div className="rounded bg-emerald-950/40 border border-emerald-800 p-2 text-emerald-200">
          Exported {r.activityCount} CTR(s), {r.partyCount} parties, total ${r.totalAmount}. Batch {r.batchId}.
        </div>
      )}
      {(r.validations || []).some((v) => (v.warnings || []).length) && (
        <div className="rounded bg-amber-950/40 border border-amber-800 p-2">
          <div className="text-amber-300 font-medium mb-1">Warnings</div>
          {(r.validations || []).flatMap((v) => v.warnings || []).map((w, i) => (
            <div key={i} className="text-amber-200">• {w}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, disabled, type = 'text', placeholder }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-gray-400 mb-0.5">{label}</span>
      <input type={type} value={value ?? ''} disabled={disabled} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100 disabled:opacity-60" />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Patron directory picker
// ---------------------------------------------------------------------------
// Search saved identities and prefill the sheet, or save this sheet's identity
// back for next time. The point is that a returning patron is selected, not
// re-keyed at 2am off a driver's licence.
//
// What it deliberately does NOT do: fill in the SSN or ID number. Those come
// back from the directory MASKED, and the mask is not a value - writing it into
// the form would turn "•••••••6789" into the stored SSN on the next save. The
// backend ignores a masked value on save for the same reason, but the honest
// thing here is to leave the field empty and say so, so the banker knows to
// re-key it from the ID in front of them. Which is also what a compliance
// person would want: the number on the sheet was read off the document that
// day, not inherited from a record.
// ----------------------------------------------------------------
// IdScanStrip - the ID copies held for a directory patron.
// ----------------------------------------------------------------
// Two different permissions live in this one component, on purpose:
//   - the LIST (when, what, expiry, who captured it) is title31:view
//   - the IMAGE takes title31:pii:reveal, comes back as a 60-second signed
//     URL, and writes an access-log row before the URL exists
// Seeing someone's driver licence is not a smaller act than seeing their SSN,
// so it is not a smaller permission.
function IdScanStrip({ patronId, canOperate }) {
  const { can } = useAuth();
  const canReveal = can('title31:pii:reveal');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [expiresOn, setExpiresOn] = useState('');
  const [unavailable, setUnavailable] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!patronId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/title31/patrons/${patronId}/ids`);
      setRows(data.ids || []);
      setUnavailable(null);
    } catch (e) {
      setRows([]);
      setUnavailable(e?.response?.data?.error || 'Could not load ID scans');
    } finally { setLoading(false); }
  }, [patronId]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                 // so re-picking the same file fires again
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`That file is ${(file.size / 1024 / 1024).toFixed(1)} MB; the limit is 10 MB`);
      return;
    }
    setUploading(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(new Error('Could not read that file'));
        r.readAsDataURL(file);
      });
      const res = await api.post(`/admin/title31/patrons/${patronId}/ids`, {
        data,
        idExpiresOn: expiresOn || null,
      });
      toast.success(`Saved as version ${res.data.id.version}`);
      setExpiresOn('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const viewOne = async (r) => {
    setViewing(r.id);
    try {
      const { data } = await api.post(`/admin/title31/patrons/${patronId}/ids/${r.id}/view`, {});
      // Opened, not embedded: the URL dies in 60 seconds and an <img> that
      // outlives it just shows a broken frame.
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not open that image');
    } finally { setViewing(null); }
  };

  if (!patronId) return null;

  const current = rows.find((r) => r.current);

  return (
    <div className="mb-3 rounded-lg border border-gray-800 bg-gray-900/40 p-3">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-300">
          <ScanLine size={14} /> ID on file
        </span>
        {loading && <Loader2 size={13} className="animate-spin text-gray-500" />}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[11px] text-gray-500 flex items-center gap-1">
            Expires
            <input
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              disabled={!canOperate || uploading}
              className="bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-xs text-gray-100"
            />
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,application/pdf"
            capture="environment"
            onChange={onFile}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={!canOperate || uploading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-gray-800 border border-gray-700 text-xs text-gray-200 hover:text-white disabled:opacity-60"
          >
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? 'Uploading…' : 'Capture new ID'}
          </button>
        </div>
      </div>

      {unavailable && (
        <div className="text-[11px] text-amber-400 mb-2">{unavailable}</div>
      )}

      {current && current.expired && (
        <div className="mb-2 rounded border border-red-900/60 bg-red-950/40 px-2.5 py-1.5 text-[11px] text-red-300">
          ID on file expired {current.idExpiresOn} — scan a new one.
        </div>
      )}

      {!loading && rows.length === 0 && !unavailable && (
        <div className="text-[11px] text-gray-500">
          No ID copy held for this patron.
        </div>
      )}

      {rows.length > 0 && (
        <div className="divide-y divide-gray-800/60">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-1.5">
              <span className={`text-xs ${r.current ? 'text-gray-100' : 'text-gray-500'}`}>
                v{r.version}{r.current ? ' · current' : ''}
              </span>
              <span className="text-[11px] text-gray-500">
                {[r.idType, r.idIssuer].filter(Boolean).join(' · ') || '—'}
              </span>
              <span className="text-[11px] text-gray-500">
                captured {String(r.capturedAt).slice(0, 10)}
              </span>
              {r.idExpiresOn && (
                <span className={`text-[11px] ${r.expired ? 'text-red-400' : 'text-gray-500'}`}>
                  {r.expired ? 'expired' : 'valid to'} {r.idExpiresOn}
                </span>
              )}
              {r.byteSize ? <span className="text-[11px] text-gray-600">{Math.round(r.byteSize / 1024)} KB</span> : null}
              <div className="ml-auto">
                {!r.available ? (
                  <span className="text-[11px] text-gray-600">
                    purged {String(r.purgedAt).slice(0, 10)}
                  </span>
                ) : canReveal ? (
                  <button
                    type="button"
                    onClick={() => viewOne(r)}
                    disabled={viewing === r.id}
                    title="Opens for 60 seconds. This view is logged."
                    className="flex items-center gap-1 px-2 py-0.5 rounded bg-gray-800 border border-gray-700 text-[11px] text-gray-200 hover:text-white disabled:opacity-60"
                  >
                    {viewing === r.id ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} View
                  </button>
                ) : (
                  <span className="text-[11px] text-gray-600">view restricted</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 text-[11px] text-gray-600">
        Copies are kept in a private bucket, never overwritten, and destroyed 5 years
        after capture. Opening one is logged against your account.
      </div>
    </div>
  );
}

function PatronPicker({ sheetId, canOperate, form, setForm, onSaved }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingDir, setSavingDir] = useState(false);
  const boxRef = useRef(null);

  // Debounced search - this fires on every keystroke otherwise, and the list is
  // small enough that it is not worth hammering.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get('/admin/title31/patrons', { params: { q, limit: 15 } });
        setRows(data.patrons || []);
      } catch {
        setRows([]);
      } finally { setBusy(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  // Close on an outside click, so the list does not sit over the form.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = async (p) => {
    setForm((f) => ({
      ...f,
      name: p.name ?? '', address: p.address ?? '', city: p.city ?? '', state: p.state ?? '',
      postalCode: p.postalCode ?? '', country: p.country ?? 'US', dateOfBirth: p.dateOfBirth ?? '',
      idType: p.idType ?? '', idIssuer: p.idIssuer ?? '',
      sex: p.sex ?? '', race: p.race ?? '', height: p.height ?? '', weight: p.weight ?? '',
      eyeColor: p.eyeColor ?? '', hairColor: p.hairColor ?? '', glasses: p.glasses ?? '',
      facialHair: p.facialHair ?? '', otherDescriptors: p.otherDescriptors ?? '',
      // idNumber and tin intentionally left alone - see the note above.
    }));
    setOpen(false);
    // Record which directory entry filled this sheet, and which ID scan was
    // current when it did. Usage counter is the least interesting part.
    try {
      await api.post(`/admin/title31/patrons/${p.id}/used`, { patronDayId: sheetId });
      setForm((f) => ({ ...f, patronDirectoryId: p.id }));
    } catch { /* back-link is a nicety; do not block the fill */ }
    const bits = [];
    if (p.hasIdNumber) bits.push('ID number');
    if (p.hasTin) bits.push('SSN');
    toast.success(
      bits.length
        ? `Filled from ${p.name} — re-enter the ${bits.join(' and ')} from the ID`
        : `Filled from ${p.name}`
    );
  };

  const saveToDirectory = async () => {
    setSavingDir(true);
    try {
      const { data } = await api.post('/admin/title31/patrons/from-sheet', { patronDayId: sheetId });
      toast.success(`Saved ${data.patron.name} to the directory`);
      setForm((f) => ({ ...f, patronDirectoryId: data.patron.id }));
      if (onSaved) onSaved(data.patron);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not save to the directory');
    } finally { setSavingDir(false); }
  };

  const expiredNote = (p) => {
    if (!p.idExpiry || !p.idExpiry.known) return null;
    return p.idExpiry.expired
      ? <span className="text-red-400">ID expired {p.idExpiry.expiresOn}</span>
      : <span className="text-gray-600">ID valid to {p.idExpiry.expiresOn}</span>;
  };

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" ref={boxRef}>
      <div className="relative">
        <button
          type="button"
          onClick={() => { setOpen((o) => !o); setQ(''); }}
          disabled={!canOperate}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-gray-800 border border-gray-700 text-sm text-gray-200 hover:text-white disabled:opacity-60"
        >
          <Users size={15} /> Fill from directory
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-[360px] rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
            <div className="p-2 border-b border-gray-800">
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, or last 4 of SSN"
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-gray-100"
              />
            </div>
            <div className="max-h-72 overflow-y-auto">
              {busy && <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>}
              {!busy && rows.length === 0 && (
                <div className="px-3 py-3 text-xs text-gray-500">
                  {q ? 'No saved patron matches that.' : 'No saved patrons yet — fill in a sheet and use “Save to directory”.'}
                </div>
              )}
              {rows.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pick(p)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-800 border-b border-gray-800/60"
                >
                  <div className="text-sm text-gray-100">{p.name}</div>
                  <div className="text-[11px] text-gray-500 flex flex-wrap gap-x-2">
                    {p.city ? <span>{p.city}{p.state ? `, ${p.state}` : ''}</span> : null}
                    {p.dateOfBirth ? <span>DOB {p.dateOfBirth}</span> : null}
                    {p.tinLast4 ? <span>SSN ••••{p.tinLast4}</span> : null}
                    {p.timesUsed ? <span>used {p.timesUsed}×</span> : null}
                  </div>
                  {expiredNote(p) && <div className="text-[11px] mt-0.5">{expiredNote(p)}</div>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={saveToDirectory}
        disabled={!canOperate || savingDir || !form.name}
        title={!form.name ? 'Enter a name on this sheet first' : 'Save this identity for next time'}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-gray-800 border border-gray-700 text-sm text-gray-200 hover:text-white disabled:opacity-60"
      >
        <UserPlus size={15} /> {savingDir ? 'Saving…' : 'Save to directory'}
      </button>

      {(form.hasTin || form.hasIdNumber) && (
        <span className="text-[11px] text-gray-500">
          SSN and ID number are stored encrypted and shown masked.
        </span>
      )}
      {(form.tinUnreadable || form.idNumberUnreadable) && (
        <span className="text-[11px] text-amber-400">
          A stored value cannot be decrypted on this server — check the encryption key.
        </span>
      )}
    </div>
  );
}

function SheetEditor(props) {
  const {
    sheet, form, setForm, canOperate, canFile, saving, onSave, onSign, onDelete,
    line, setLine, onAddLine, onDeleteLine, amtRef, ctrThresholdCents, pct, statusColor, onGenerateCtr,
  } = props;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const flagged = sheet.threshold && sheet.threshold.triggerType;
  const lines = sheet.transactions || [];

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900 p-4 space-y-5">
      {/* header + totals */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-800 pb-3">
        <div>
          <div className="text-xs text-gray-500">Gaming day {sheet.gamingDate} · {sheet.location}</div>
          <div className="text-lg font-semibold text-gray-100">{form.name || 'Unidentified patron'}</div>
        </div>
        <div className="flex gap-5 text-right">
          <div>
            <div className="text-[10px] uppercase text-gray-500">Cash In</div>
            <div className={`text-lg font-bold ${statusColor(sheet.totalCashInCents)}`}>{fmtCents(sheet.totalCashInCents)}</div>
            <Meter pct={pct(sheet.totalCashInCents)} over={sheet.totalCashInCents > ctrThresholdCents} />
          </div>
          <div>
            <div className="text-[10px] uppercase text-gray-500">Cash Out</div>
            <div className={`text-lg font-bold ${statusColor(sheet.totalCashOutCents)}`}>{fmtCents(sheet.totalCashOutCents)}</div>
            <Meter pct={pct(sheet.totalCashOutCents)} over={sheet.totalCashOutCents > ctrThresholdCents} />
          </div>
        </div>
      </div>

      {flagged && (
        <div className="rounded bg-red-950/50 border border-red-800 p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-red-200 text-sm">
            <AlertTriangle size={18} className="text-red-400" />
            Exceeds the CTR threshold ({sheet.threshold.triggerType.replace('_', ' ')}). A FinCEN Form 112 (CTR) is required.
          </div>
          {canFile && !sheet.ctrFiled && (
            <button onClick={onGenerateCtr} className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-sm whitespace-nowrap">
              Generate CTR
            </button>
          )}
        </div>
      )}

      {/* Transaction lines */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-2">Cash transactions</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-gray-800">
                <th className="py-1 pr-2">In/Out</th><th className="py-1 pr-2">Amount</th>
                <th className="py-1 pr-2">Time</th><th className="py-1 pr-2">Initials</th>
                <th className="py-1 pr-2">Type</th><th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((t) => (
                <tr key={t.id} className="border-b border-gray-800/60">
                  <td className="py-1 pr-2">
                    <span className={`px-1.5 py-0.5 rounded text-[11px] ${t.direction === 'in' ? 'bg-emerald-900/60 text-emerald-300' : 'bg-blue-900/60 text-blue-300'}`}>
                      {t.direction === 'in' ? 'Cash In' : 'Cash Out'}
                    </span>
                  </td>
                  <td className="py-1 pr-2 text-gray-100">{fmtCents(t.amountCents)}</td>
                  <td className="py-1 pr-2 text-gray-300">{t.timeText || '—'}</td>
                  <td className="py-1 pr-2 text-gray-300">{t.initials || '—'}</td>
                  <td className="py-1 pr-2 text-gray-400">{t.txnType || '—'}</td>
                  <td className="py-1 text-right">
                    {canOperate && (
                      <button onClick={() => onDeleteLine(t.id)} className="text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={6} className="py-2 text-gray-500 text-sm">No transactions logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {canOperate && (
          <form onSubmit={onAddLine} className="flex flex-wrap items-end gap-2 mt-3 bg-gray-800/50 rounded p-2">
            <label className="text-xs text-gray-400">
              <span className="block mb-0.5">In/Out</span>
              <select value={line.direction} onChange={(e) => setLine({ ...line, direction: e.target.value })}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100">
                <option value="in">Cash In</option><option value="out">Cash Out</option>
              </select>
            </label>
            <label className="text-xs text-gray-400">
              <span className="block mb-0.5">Amount ($)</span>
              <input ref={amtRef} type="number" step="0.01" value={line.amount}
                onChange={(e) => setLine({ ...line, amount: e.target.value })}
                className="w-28 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" />
            </label>
            <label className="text-xs text-gray-400">
              <span className="block mb-0.5">Time</span>
              <input value={line.timeText} placeholder="5:18PM"
                onChange={(e) => setLine({ ...line, timeText: e.target.value })}
                className="w-24 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" />
            </label>
            <label className="text-xs text-gray-400">
              <span className="block mb-0.5">Initials</span>
              <input value={line.initials} maxLength={10}
                onChange={(e) => setLine({ ...line, initials: e.target.value })}
                className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" />
            </label>
            <label className="text-xs text-gray-400">
              <span className="block mb-0.5">Type</span>
              <input value={line.txnType} placeholder="Buy-in"
                onChange={(e) => setLine({ ...line, txnType: e.target.value })}
                className="w-28 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" />
            </label>
            <button type="submit" className="flex items-center gap-1 px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm">
              <Plus size={15} /> Add
            </button>
          </form>
        )}
      </div>

      {/* Identity */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-2">Patron identity</h3>
        <PatronPicker sheetId={sheet.id} canOperate={canOperate} form={form} setForm={setForm} />
        {form.patronDirectoryId && (
          <IdScanStrip patronId={form.patronDirectoryId} canOperate={canOperate} />
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {ID_FIELDS.map(([k, label]) => (
            <Field key={k} label={label} value={form[k]} disabled={!canOperate}
              type={k === 'dateOfBirth' ? 'date' : 'text'} onChange={set(k)} />
          ))}
        </div>
      </div>

      {/* Physical description */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-2">Physical description</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {DESC_FIELDS.map(([k, label]) => (
            <Field key={k} label={label} value={form[k]} disabled={!canOperate} onChange={set(k)} />
          ))}
        </div>
      </div>

      {/* Location + attestation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Field label="Table" value={form.tableNumber} disabled={!canOperate} onChange={set('tableNumber')} />
        <Field label="Seat" value={form.seatNumber} disabled={!canOperate} onChange={set('seatNumber')} />
        <Field label="Owner/Key Employee" value={form.keyEmployee} disabled={!canOperate} onChange={set('keyEmployee')} />
        <label className="flex items-center gap-2 mt-5 text-sm text-gray-300">
          <input type="checkbox" checked={!!form.sarFlagged} disabled={!canOperate}
            onChange={(e) => setForm((f) => ({ ...f, sarFlagged: e.target.checked }))} />
          SAR flag
        </label>
      </div>
      <Field label="Notes" value={form.notes} disabled={!canOperate} onChange={set('notes')} />

      {/* Footer actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-800 pt-3">
        <div className="text-xs text-gray-500">
          {sheet.signature ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 size={14} /> {sheet.signature}{sheet.signedAt ? ` · ${new Date(sheet.signedAt).toLocaleString()}` : ''}</span> : 'Not signed'}
          {sheet.ctrFiled && <span className="ml-3 text-amber-400">CTR filed</span>}
        </div>
        {canOperate && (
          <div className="flex items-center gap-2">
            <button onClick={onDelete} className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-gray-400 hover:text-red-400 text-sm">
              <Trash2 size={14} /> Delete
            </button>
            <button onClick={onSign} className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-gray-200 hover:text-white text-sm">
              <PenLine size={14} /> Sign
            </button>
            <button onClick={onSave} disabled={saving} className="flex items-center gap-1 px-4 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium disabled:opacity-60">
              {saving ? 'Saving…' : 'Save sheet'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Meter({ pct, over }) {
  return (
    <div className="w-24 h-1.5 rounded-full bg-gray-700 mt-1 overflow-hidden">
      <div className={`h-full ${over ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function ConfigModal({ form, setForm, onClose, onSave }) {
  const setFiler = (k) => (e) => setForm((f) => ({ ...f, filer: { ...f.filer, [k]: e.target.value } }));
  const setTx = (k) => (e) => setForm((f) => ({ ...f, transmitter: { ...f.transmitter, [k]: e.target.value } }));
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-100">Title 31 settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <h3 className="text-sm font-semibold text-gray-300 mb-2">Thresholds</h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="MTL logging floor ($)" type="number" value={form.mtlLogFloorDollars}
            onChange={(v) => setForm((f) => ({ ...f, mtlLogFloorDollars: v }))} />
          <Field label="CTR threshold ($, exceeds)" type="number" value={form.ctrThresholdDollars}
            onChange={(v) => setForm((f) => ({ ...f, ctrThresholdDollars: v }))} />
        </div>

        <h3 className="text-sm font-semibold text-gray-300 mb-2">Filer (Reporting Financial Institution)</h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="text-xs text-gray-400"><span className="block mb-0.5">Legal name</span><input value={form.filer.legalName || ''} onChange={setFiler('legalName')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">DBA</span><input value={form.filer.dba || ''} onChange={setFiler('dba')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">EIN</span><input value={form.filer.ein || ''} onChange={setFiler('ein')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">Type of institution</span><input value={form.filer.typeOfGamingInstitution || ''} onChange={setFiler('typeOfGamingInstitution')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">Address</span><input value={form.filer.address || ''} onChange={setFiler('address')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">City</span><input value={form.filer.city || ''} onChange={setFiler('city')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">State</span><input value={form.filer.state || ''} onChange={setFiler('state')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">ZIP</span><input value={form.filer.postalCode || ''} onChange={setFiler('postalCode')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">Phone</span><input value={form.filer.phone || ''} onChange={setFiler('phone')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">Contact name</span><input value={form.filer.contactName || ''} onChange={setFiler('contactName')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
        </div>

        <h3 className="text-sm font-semibold text-gray-300 mb-2">Transmitter (BSA E-Filing)</h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="text-xs text-gray-400"><span className="block mb-0.5">Transmitter name</span><input value={form.transmitter.name || ''} onChange={setTx('name')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">TCC (Transmitter Control Code)</span><input value={form.transmitter.tcc || ''} onChange={setTx('tcc')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">TIN</span><input value={form.transmitter.tin || ''} onChange={setTx('tin')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">Phone</span><input value={form.transmitter.phone || ''} onChange={setTx('phone')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
          <label className="text-xs text-gray-400"><span className="block mb-0.5">Contact name</span><input value={form.transmitter.contactName || ''} onChange={setTx('contactName')} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" /></label>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 rounded border border-gray-700 text-gray-300 text-sm">Cancel</button>
          <button onClick={onSave} className="px-4 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium">Save settings</button>
        </div>
      </div>
    </div>
  );
}
